/**
 * Copyright 2026 Google LLC.
 * Copyright (c) Microsoft Corporation.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * THIS FILE IS AUTOGENERATED by cddlconv 0.2.0.
 * Run `tools/update_bidi_types.py` to regenerate.
 * @see https://github.com/w3c/webdriver-bidi/blob/master/index.bs
 */

import * as z from 'zod';

export const PermissionsCommandSchema: z.ZodLazy<
  typeof Permissions.SetPermissionSchema
> = z.lazy(() => Permissions.SetPermissionSchema);
export namespace Permissions {
  export const PermissionDescriptorSchema: z.ZodLazy<
    z.ZodObject<{
      name: z.ZodString;
    }>
  > = z.lazy(() =>
    z.object({
      name: z.string(),
    }),
  );
}
export namespace Permissions {
  export const PermissionStateSchema: z.ZodLazy<
    z.ZodEnum<{granted: 'granted'; denied: 'denied'; prompt: 'prompt'}>
  > = z.lazy(() => z.enum(['granted', 'denied', 'prompt']));
}
export namespace Permissions {
  export const SetPermissionSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'permissions.setPermission'>;
      params: typeof Permissions.SetPermissionParametersSchema;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('permissions.setPermission'),
      params: Permissions.SetPermissionParametersSchema,
    }),
  );
}
export namespace Permissions {
  export const SetPermissionParametersSchema: z.ZodLazy<
    z.ZodObject<{
      descriptor: typeof Permissions.PermissionDescriptorSchema;
      state: typeof Permissions.PermissionStateSchema;
      origin: z.ZodString;
      embeddedOrigin: z.ZodOptional<z.ZodString>;
      userContext: z.ZodOptional<z.ZodString>;
    }>
  > = z.lazy(() =>
    z.object({
      descriptor: Permissions.PermissionDescriptorSchema,
      state: Permissions.PermissionStateSchema,
      origin: z.string(),
      embeddedOrigin: z.string().optional(),
      userContext: z.string().optional(),
    }),
  );
}
